// A live check against GitHub's own commit history for `main`, as a
// complementary signal to the service worker's own byte-diff update check
// (UpdateManager.checkForUpdate) — that one only ever detects an update once
// Netlify has actually finished building and deploying it, so a "you're up
// to date" right after merging a PR is ambiguous: is main genuinely
// unchanged, or has the new build just not gone live yet? This answers that
// directly from GitHub itself.
//
// Read-only, unauthenticated GitHub REST call to a PUBLIC repo — no token
// needed, but subject to GitHub's unauthenticated rate limit (60 requests/
// hour per IP). Only ever call this from the manual "Check for Update"
// button tap, never from the automatic hourly background check.
const REPO = 'protocol-commits/maximum-band';

export interface GithubUpdateStatus {
  /** GitHub's latest commit SHA on main, shortened to match APP_VERSION's own length. */
  latestSha: string;
  /** True when GitHub's latest commit differs from the currently running build. */
  isNewer: boolean;
}

export async function checkGithubForNewerCommit(currentSha: string): Promise<GithubUpdateStatus | null> {
  try {
    const response = await fetch(`https://api.github.com/repos/${REPO}/commits/main`, {
      headers: { Accept: 'application/vnd.github+json' },
    });
    if (!response.ok) return null;
    const data: unknown = await response.json();
    const sha = typeof data === 'object' && data !== null && 'sha' in data ? (data as { sha: unknown }).sha : null;
    if (typeof sha !== 'string' || sha.length < 7) return null;
    const latestSha = sha.slice(0, 7);
    return { latestSha, isNewer: currentSha !== 'unknown' && latestSha !== currentSha };
  } catch {
    // Offline, CORS hiccup, GitHub rate-limited us, whatever — this is a
    // best-effort supplementary signal, never something to surface as an
    // app error.
    return null;
  }
}
